import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ShoppingCartComponent } from '../shopping-cart/shopping-cart.component';
import { AuthStateService, getEmailFromToken } from '@hypenation/auth';
import { Cart, CartStateService } from '@hypenation/tickets';
import { Subscription } from 'rxjs';

export interface NavigationLink {
  route: string;
  name: string;
  external?: boolean;
}

@Component({
  selector: 'hypenation-shared-private-events-navbar',
  standalone: true,
  imports: [CommonModule, ShoppingCartComponent],
  templateUrl: './private-events-navbar.component.html',
  styleUrl: './private-events-navbar.component.css',
})
export class PrivateEventsNavbarComponent implements OnInit, OnDestroy {
  @Input() logoUrl: string = '';
  @Input() logoAlt: string = 'Logo';
  @Input() navigationLinks: NavigationLink[] = [];
  @Input() homeRoute: string = '/';

  isMobileMenuOpen = false;
  isUserMenuOpen = false;
  userEmail: string | null = null;
  userInitials: string = '';

  cart: Cart | null = null;
  numberOfItems = 0;

  private readonly _authStateService = inject(AuthStateService);
  private documentClickHandler = (event: MouseEvent) =>
    this.onDocumentClick(event);
  private authStateSubscription?: Subscription;
  private cartUpdateSubscription?: Subscription;

  constructor(
    private readonly _router: Router,
    private readonly _cartStateService: CartStateService
  ) {}

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  toggleUserMenu() {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  closeUserMenu() {
    this.isUserMenuOpen = false;
  }

  get activeLink() {
    return this._router.url;
  }

  public navigate(route: NavigationLink): void {
    if (route.external) {
      window.open(route.route, '_blank');
    } else {
      this._router.navigateByUrl(route.route);
      this.isMobileMenuOpen = false;
    }
  }

  public navigateToCart(): void {
    if (this.cart) {
      this._router.navigateByUrl('/cart/' + this.cart.uid);
    }
  }

  ngOnInit(): void {
    this.loadCart();
    this.checkAuthState();
    // Close user menu when clicking outside
    document.addEventListener('click', this.documentClickHandler);

    // Subscribe to auth state changes
    this.authStateSubscription = this._authStateService
      .onAuthStateChange()
      .subscribe(() => {
        this.checkAuthState();
      });

    // Subscribe to cart updates
    this.cartUpdateSubscription = this._cartStateService.cartUpdate$.subscribe(
      () => {
        this.syncCartFromState();
      }
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.documentClickHandler);
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }
    if (this.cartUpdateSubscription) {
      this.cartUpdateSubscription.unsubscribe();
    }
  }

  checkAuthState(): void {
    const token = this._authStateService.getToken();
    if (token) {
      this.userEmail = getEmailFromToken(token);
      if (this.userEmail) {
        // Generate initials from email
        const parts = this.userEmail.split('@')[0].split('.');
        if (parts.length >= 2) {
          this.userInitials = (parts[0][0] + parts[1][0]).toUpperCase();
        } else {
          this.userInitials = this.userEmail.substring(0, 2).toUpperCase();
        }
      }
    } else {
      // Clear user info if not authenticated
      this.userEmail = null;
      this.userInitials = '';
    }
  }

  isAuthenticated(): boolean {
    return this._authStateService.isAuthenticated();
  }

  logout(): void {
    this._authStateService.removeTokens();
    this.userEmail = null;
    this.userInitials = '';
    this.isUserMenuOpen = false;
    // Redirect to home route after logout
    this._router.navigate([this.homeRoute || '/']);
  }

  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const userMenuElement = target.closest('.user-menu-container');
    if (!userMenuElement && this.isUserMenuOpen) {
      this.closeUserMenu();
    }
  }

  private loadCart(): void {
    this._cartStateService.resolveCart().subscribe({
      next: (cart) => {
        this.cart = cart;
        this.numberOfItems = cart?.numberOfItems ?? 0;
      },
      error: (err) => {
        console.error('Error resolving cart:', err);
      },
    });
  }

  private syncCartFromState(): void {
    const cart = this._cartStateService.cart();
    if (cart) {
      this.cart = cart;
      this.numberOfItems = cart.numberOfItems ?? 0;
      return;
    }

    this.cart = null;
    this.numberOfItems = 0;
  }
}
